/**
 * Périmètre de migration V1 → V2 : quelles catégories de lots sont concernées
 * selon les modules V2 souscrits par l'école.
 *
 * ⚠️ La table de correspondance ci-dessous est dupliquée côté back dans
 * `App\Migration\Service\ModulePerimeterResolver::CATEGORY_TO_V2_MODULE`.
 * Garder les deux synchronisées.
 */

/**
 * Catégorie de lot → code du module V2 qui la gouverne. Une catégorie dont le
 * module n'est pas souscrit par l'école est « hors périmètre » : elle est
 * masquée côté UI et exclue de l'avancement global.
 */
export const CATEGORY_TO_V2_MODULE: Record<string, string> = {
  configuration: 'GESTION_ECOLE',
  users: 'GESTION_ECOLE',
  school_structure: 'GESTION_ECOLE',
  documents: 'GESTION_ECOLE',
  notifications: 'GESTION_ECOLE',
  time_management: 'GESTION_TEMPS',
  evaluations: 'EVALUATION',
  messaging: 'COMMUNICATION',
  montessori: 'MONTESSORI',
  billing: 'FACTU',
  student_records: 'GESTION_ECOLE',
};

/**
 * Surcharge par lot : modules V2 qui mettent CE lot dans le périmètre, quelle
 * que soit sa catégorie. Le lot est dans le périmètre si AU MOINS un des
 * modules listés est souscrit.
 *
 * Cas actuel : le référentiel de compétences EN est classé en `evaluations`
 * (sa vraie maison fonctionnelle) mais reste visible pour les écoles Montessori
 * sans module Évaluation, car les activités Montessori s'appuient dessus.
 *
 * Les échelles d'acquisition suivent exactement la même règle : le référentiel
 * de compétences en dépend (lot bloqué tant qu'elles ne sont pas migrées), donc
 * les masquer à une école Montessori sans module Évaluation rendrait ses
 * compétences définitivement inatteignables.
 *
 * ⚠️ Dupliqué côté back dans
 * `App\Migration\Service\ModulePerimeterResolver::STEP_TO_V2_MODULES`.
 * Garder les deux synchronisés.
 */
export const STEP_TO_V2_MODULES: Record<string, string[]> = {
  'montessori.competences': ['EVALUATION', 'MONTESSORI'],
  'evaluations.skill_scales': ['EVALUATION', 'MONTESSORI'],
};

/**
 * Libellés FR des modules V2 (table `module`, `is_v2 = 1`), pour affichage en
 * chips. Repli : le code brut si absent (module catalogue non listé ici).
 */
export const V2_MODULE_LABELS: Record<string, string> = {
  GESTION_ECOLE: "Gestion de l'école",
  GESTION_TEMPS: 'Gestion des temps',
  EVALUATION: 'Évaluation, Bulletins',
  COMMUNICATION: 'Communication',
  MONTESSORI: 'Montessori',
  FACTU: 'Facturation / Comptabilité',
  COMPTABILITE: 'Comptabilité',
  COMPTE_PRO: 'Compte Pro',
  CONTENUS_PEDAGOGIQUES: 'Contenus pédagogiques',
  EXTRA_SCOLAIRE: 'Extra-scolaire',
  INSCRIPTIONS: 'Inscriptions',
  STAGES: 'Stages / apprentissage',
  EMPLOYEUR: 'Employeur',
};

/** Libellé FR d'un module V2, ou le code brut si non répertorié. */
export function v2ModuleLabel(code: string): string {
  return V2_MODULE_LABELS[code] ?? code;
}

/**
 * Catégories de lots « disponibles prochainement » : la migration de ces
 * périmètres n'est pas encore ouverte côté produit. Les lots concernés sont
 * affichés grisés (non lançables) et exclus des stats et de l'avancement.
 *
 * ⚠️ Dupliqué côté back dans
 * `App\Migration\Service\ModulePerimeterResolver` (`COMING_SOON_CATEGORIES` /
 * `COMING_SOON_STEP_CODES`). Garder les deux synchronisés.
 */
export const COMING_SOON_CATEGORIES = new Set(['messaging', 'notifications']);

/**
 * Codes de lots individuels « disponibles prochainement », pour désactiver un
 * lot précis sans griser toute sa catégorie.
 */
export const COMING_SOON_STEP_CODES = new Set([
  'evaluations.report_card_templates',
  // Factures de solde suspendues (08/10/2026) : le lot fermait la facturation V1.
  // Les référentiels de facturation restent migrables. Réactiver avec
  // V1_BILLING_LOCKDOWN_ENABLED au moment de la bascule.
  'billing.balance_invoices',
  'montessori.suivis',
  'montessori.suivi_photos',
  'montessori.plans',
]);

/**
 * Fermeture de la facturation V1 (« Fermer la facturation V1 ») suspendue avec
 * le lot des factures de solde — miroir de `V1BillingLockdownService::APPLY_ENABLED`.
 */
export const V1_BILLING_LOCKDOWN_ENABLED = false;

/** Le lot est-il « disponible prochainement » (désactivé produit) ? */
export function isStepComingSoon(
  category: string | null | undefined,
  stepCode: string | null | undefined
): boolean {
  if (category && COMING_SOON_CATEGORIES.has(category)) return true;
  return !!stepCode && COMING_SOON_STEP_CODES.has(stepCode);
}

/** La catégorie entière est-elle « disponible prochainement » ? */
export function isCategoryComingSoon(category: string | null | undefined): boolean {
  return !!category && COMING_SOON_CATEGORIES.has(category);
}

/**
 * Une catégorie est-elle hors du périmètre de migration au vu des modules V2
 * souscrits ? `activeV2Modules == null` ⇒ périmètre indéterminé ⇒ tout inclus
 * (fallback sûr : on ne masque jamais par erreur).
 */
export function isCategoryOutOfPerimeter(
  category: string | null | undefined,
  activeV2Modules: string[] | null | undefined
): boolean {
  if (!activeV2Modules) return false;
  if (!category) return false;
  const mod = CATEGORY_TO_V2_MODULE[category];
  if (!mod) return false;
  return !activeV2Modules.includes(mod);
}

/**
 * Un lot est-il hors périmètre ? Surcharge `STEP_TO_V2_MODULES` si son code y
 * figure (hors périmètre seulement si AUCUN des modules listés n'est souscrit),
 * sinon on retombe sur le périmètre de sa catégorie. Miroir de
 * `ModulePerimeterResolver::isStepInPerimeter`.
 */
export function isStepOutOfPerimeter(
  category: string | null | undefined,
  stepCode: string | null | undefined,
  activeV2Modules: string[] | null | undefined
): boolean {
  if (!activeV2Modules) return false;
  const override = stepCode ? STEP_TO_V2_MODULES[stepCode] : undefined;
  if (override) return !override.some((m) => activeV2Modules.includes(m));
  return isCategoryOutOfPerimeter(category, activeV2Modules);
}

/**
 * Codes des lots utilisateurs qui soumettent des jobs field-values V2
 * asynchrones (champs perso/natifs) — la sous-ligne d'avancement
 * {@link FieldValueJobsRow} n'est rendue que sous ces lots.
 */
export function isUserLotCode(code: string): boolean {
  return (
    code === 'students.eleves' ||
    code === 'students.third_parties' ||
    code === 'parents.fathers' ||
    code === 'parents.mothers' ||
    (code.startsWith('users.') && code !== 'users.photos')
  );
}
